import { useId, type ReactNode } from 'react';
import { Chip } from './Chip';
import { FieldMessage, fieldDescription } from './TextField';
import type { SelectOption } from './Select';

export interface ChipGroupProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Visible label; without it give `aria-label`. */
  label?: ReactNode;
  'aria-label'?: string;
  hint?: ReactNode;
  size?: 'sm' | 'md';
  /** false = one value at most (picking another replaces it, picking it again clears it). */
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Toggle chips for a short, always visible set of options (filters, scopes): one `aria-pressed` button each in a
 * labelled group. Options keep their order; `value` keeps the order of `options`. Long lists → `MultiSelect`.
 */
export function ChipGroup({
  options,
  value,
  onChange,
  label,
  hint,
  size = 'sm',
  multiple = true,
  disabled,
  className,
  ...rest
}: ChipGroupProps) {
  const labelId = useId();
  const ids = fieldDescription(useId(), hint, undefined);
  const toggle = (v: string) => {
    const on = value.includes(v);
    if (!multiple) return onChange(on ? [] : [v]);
    const next = on ? value.filter((x) => x !== v) : [...value, v];
    // the options' order, values not among the options (kept) last
    const order = new Map(options.map((o, i) => [o.value, i]));
    onChange(next.sort((a, b) => (order.get(a) ?? Infinity) - (order.get(b) ?? Infinity)));
  };
  return (
    <div className={['nyk-ui-field', 'nyk-ui-chip-group', className].filter(Boolean).join(' ')}>
      {label && <span id={labelId}>{label}</span>}
      <div
        role="group"
        className="nyk-chips"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : rest['aria-label']}
        aria-describedby={ids.describedBy}
        data-testid={rest['data-testid']}
      >
        {options.map((o) => (
          <Chip
            key={o.value}
            size={size}
            selected={value.includes(o.value)}
            disabled={disabled || o.disabled}
            onClick={() => toggle(o.value)}
          >
            {o.label}
          </Chip>
        ))}
      </div>
      <FieldMessage hint={hint} ids={ids} />
    </div>
  );
}
